import {apiFetch} from '../fetch'
import type {CreateOrganizationRequest, UpdateOrganizationRequest} from './models/organization'
import {
    CreateOrganizationRequestSchema,
    CsvPreviewResponseSchema,
    OrganizationDetailSchema,
    OrganizationListItemResponseSchema,
    UpdateOrganizationRequestSchema,
    UploadEventListItemResponseSchema,
    UploadProspectsResponseSchema,
    UploadTemplateHeadersSchema,
} from './models/organization'
import {UserDetailSchema, UserListItemResponseSchema} from './models/user'

export async function listOrganizations() {
    const data = await apiFetch<unknown>('/admin/organizations')
    return OrganizationListItemResponseSchema.parse(data)
}

export async function createOrganization(body: CreateOrganizationRequest) {
    const bodyApi = CreateOrganizationRequestSchema.parse(body)
    const data = await apiFetch<unknown>('/admin/organizations', {
        method: 'POST',
        body: JSON.stringify(bodyApi),
    })
    return OrganizationDetailSchema.parse(data)
}

export async function getOrganizationDetail(orgId: string) {
    const data = await apiFetch<unknown>(`/admin/organizations/${orgId}`)
    return OrganizationDetailSchema.parse(data)
}

export async function deactivateOrganization(orgId: string) {
    await apiFetch(`/admin/organizations/${orgId}/deactivate`, {method: 'POST'})
}

export async function reactivateOrganization(orgId: string) {
    await apiFetch(`/admin/organizations/${orgId}/reactivate`, {method: 'POST'})
}

export async function updateOrganization(orgId: string, body: UpdateOrganizationRequest) {
    const bodyApi = UpdateOrganizationRequestSchema.parse(body)
    const data = await apiFetch<unknown>(`/admin/organizations/${orgId}`, {
        method: 'PATCH',
        body: JSON.stringify(bodyApi),
    })
    return OrganizationDetailSchema.parse(data)
}

export async function listUsers(includeOrgUsers?: boolean) {
    const params = includeOrgUsers ? '?include_org_users=true' : ''
    const data = await apiFetch<unknown>(`/admin/users${params}`)
    return UserListItemResponseSchema.parse(data)
}

export async function listOrganizationUsers(orgId: string) {
    const data = await apiFetch<unknown>(`/admin/organizations/${orgId}/users`)
    return UserListItemResponseSchema.parse(data)
}

export async function getUserDetail(userId: string) {
    const data = await apiFetch<unknown>(`/admin/users/${userId}`)
    return UserDetailSchema.parse(data)
}

export async function editUser(userId: string, body: {name: string; email: string}) {
    const data = await apiFetch<unknown>(`/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    })
    return UserDetailSchema.parse(data)
}

export async function inviteUser(body: {email: string; name: string}) {
    const data = await apiFetch<unknown>('/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return UserDetailSchema.parse(data)
}

export async function inviteOrganizationUser(orgId: string, body: {email: string; name: string}) {
    const data = await apiFetch<unknown>(`/admin/organizations/${orgId}/users/invite`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return UserDetailSchema.parse(data)
}

export async function deactivateUser(userId: string) {
    await apiFetch(`/admin/users/${userId}/deactivate`, {method: 'POST'})
}

export async function reactivateUser(userId: string) {
    await apiFetch(`/admin/users/${userId}/reactivate`, {method: 'POST'})
}

export class UploadError extends Error {
    constructor(
        message: string,
        public readonly body?: unknown,
    ) {
        super(message)
        this.name = 'UploadError'
    }
}

export async function previewProspectsCsv(orgId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetch(`/api/admin/organizations/${orgId}/upload/preview`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
    })
    const body = await res.json()
    if (!res.ok) {
        throw new UploadError(
            (typeof body.detail === 'string' ? body.detail : body.detail?.detail) ||
                `API error: ${res.status}`,
            body.detail,
        )
    }
    return CsvPreviewResponseSchema.parse(body)
}

export async function uploadProspects(orgId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const res = await fetch(`/api/admin/organizations/${orgId}/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
    })
    const body = await res.json()
    if (!res.ok) {
        throw new UploadError(
            (typeof body.detail === 'string' ? body.detail : body.detail?.detail) ||
                `API error: ${res.status}`,
            body.detail,
        )
    }
    return UploadProspectsResponseSchema.parse(body)
}

export async function listUploadEvents(orgId: string) {
    const data = await apiFetch<unknown>(`/admin/organizations/${orgId}/uploads`)
    return UploadEventListItemResponseSchema.parse(data)
}

export async function getUploadTemplateHeaders() {
    const data = await apiFetch<unknown>('/admin/upload-template-headers')
    return UploadTemplateHeadersSchema.parse(data)
}
